import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CarCard } from "@/components/CarCard";
import { Viewer360 } from "@/components/Viewer360";
import { normalize360Frames, has360Viewer } from "@/lib/viewer360Frames";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { useFavorites } from "@/context/FavoritesContext";
import { formatPrice, formatKm } from "@/lib/format";
import { Gauge, Fuel, Calendar, Cog, Zap, Timer, Car as CarIcon, Heart, Share2, MessageCircle, Loader2, ArrowLeft, Sparkles, Image as ImageIcon, RotateCw } from "lucide-react";
import { ListingSellerPhone } from "@/components/ListingSellerPhone";
import { toast } from "sonner";
import api from "@/lib/api";
import { proxied } from "@/lib/imageProxy";
import { CarFeaturesDisplay } from "@/components/CarFeaturesDisplay";
import { FavoriteCount } from "@/components/FavoriteCount";
import { ListingMap } from "@/components/ListingMap";
import { ListingDealerPeek } from "@/components/ListingDealerPeek";
import { carPageUrl, resetPageMeta, setCarJsonLd, setPageMeta } from "@/lib/seo";
import { carAbsoluteUrl, carPath } from "@/lib/carUrl";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { TestListingBadge } from "@/components/TestListingBadge";
import { LISTING_CAR_GRID, SITE_CONTAINER } from "@/lib/siteLayout";

export default function CarDetail() {
  const { content } = useSiteContent();
  const cd = content.car_detail || DEFAULT_SITE_CONTENT.car_detail;
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openChatForCar } = useChat();
  const { isFavorite, toggleFavorite, getFavoriteCount } = useFavorites();
  const [car, setCar] = useState(null);
  const [allCars, setAllCars] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [idx, setIdx] = useState(0);
  const [view, setView] = useState("gallery"); // 'gallery' | '360'
  useEffect(() => {
    setIdx(0);
    setNotFound(false);
    setView("gallery");
    window.scrollTo(0, 0);
    api.get(`/cars/${routeRef}`).then((r) => setCar(r.data)).catch(() => setNotFound(true));
    api.post(`/cars/${routeRef}/view`).catch(() => {});
  }, [routeRef]);

  useEffect(() => {
    api.get("/cars").then((r) => setAllCars(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!car) return;
    const displayName = car.listing_title?.trim() || `${car.make} ${car.model}`;
    const title =
      car.seo_title?.trim()
      || `${displayName} ${car.year} – ${formatPrice(car.sale_price || car.price)}`;
    const description =
      car.seo_description?.trim()?.slice(0, 160)
      || car.description?.slice(0, 155)
      || `${car.make} ${car.model}, ${car.year}, ${formatKm(car.mileage)}, ${car.fuel}, ${formatPrice(car.price)} – AutoPark.`;
    const url = carPageUrl(car);
    setPageMeta({ title, description, image: car.image, url, type: "product" });
    setCarJsonLd(car, url);
    return () => resetPageMeta();
  }, [car]);

  if (notFound) {
    return (
      <div className="dark min-h-screen bg-anthracite-900 text-white">
        <Navbar />
        <div className="pt-40 pb-32 text-center px-6" data-testid="car-not-found">
          <h1 className="font-heading text-3xl font-light">{cd.not_found_title}</h1>
          <p className="text-slate-400 mt-2">{cd.not_found_body}</p>
          <Link to="/#keszlet" className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium transition-all">
            <ArrowLeft className="w-4 h-4" /> {cd.not_found_cta}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!car) {
    return (
      <div className="dark min-h-screen bg-anthracite-900 text-white">
        <Navbar />
        <div className="flex justify-center pt-48 pb-32"><Loader2 className="w-8 h-8 text-electric animate-spin" /></div>
      </div>
    );
  }

  if (car.slug && routeRef !== car.slug && routeRef === car.id) {
    return <Navigate to={carPath(car)} replace />;
  }

  const gallery = car.images && car.images.length > 0 ? car.images : [car.image];
  const frames360 = normalize360Frames(car.images_360);
  const has360 = has360Viewer(car);
  const fav = isFavorite(car.id);
  const favCount = getFavoriteCount(car.id, car.favorite_count);

  const similar = allCars
    .filter((c) => c.id !== car.id)
    .map((c) => ({
      c,
      score:
        (c.body_type === car.body_type ? 2 : 0) +
        (Math.abs(c.price - car.price) / car.price <= 0.25 ? 2 : 0) +
        (c.fuel === car.fuel ? 1 : 0) +
        (c.make === car.make ? 1 : 0),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((x) => x.c);

  const startChat = async () => {
    if (!user) { navigate("/belepes"); return; }
    try {
      await openChatForCar(car);
    } catch (e) {
      toast.error(e.response?.data?.detail || "A beszélgetés nem indítható el.");
    }
  };

  const share = async () => {
    const url = carAbsoluteUrl(car);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link a vágólapra másolva");
    } catch (_) {
      toast.info(url);
    }
  };

  const yearLabel = car.year_month ? `${car.year} / ${car.year_month}. hó` : String(car.year);
  const powerLabel = car.power_kw && car.horsepower
    ? `${car.power_kw} kW / ${car.horsepower} LE`
    : car.horsepower
      ? `${car.horsepower} LE`
      : car.power_kw
        ? `${car.power_kw} kW`
        : null;

  const specs = [
    car.trim_label ? { icon: Cog, label: "Kivitel", value: car.trim_label } : null,
    { icon: Cog, label: "Motor", value: car.engine },
    powerLabel ? { icon: Zap, label: "Teljesítmény", value: powerLabel } : null,
    car.acceleration ? { icon: Timer, label: "Gyorsulás (0-100)", value: `${car.acceleration} mp` } : null,
    car.displacement_cm3 ? { icon: Cog, label: "Hengerűrtartalom", value: `${car.displacement_cm3} cm³` } : null,
    { icon: Fuel, label: "Üzemanyag", value: car.fuel },
    { icon: Gauge, label: "Váltó", value: car.transmission },
    car.drive ? { icon: CarIcon, label: "Hajtás", value: car.drive } : null,
    { icon: Calendar, label: "Évjárat", value: yearLabel },
    { icon: CarIcon, label: "Karosszéria", value: car.body_type },
    car.doors ? { icon: CarIcon, label: "Ajtók", value: String(car.doors) } : null,
    car.seats ? { icon: CarIcon, label: "Ülések", value: `${car.seats} fő` } : null,
    car.euro_class ? { icon: Fuel, label: "Euro", value: car.euro_class } : null,
    car.condition ? { icon: CarIcon, label: "Állapot", value: car.condition } : null,
    car.tuv_valid_year
      ? {
          icon: Calendar,
          label: "Műszaki",
          value: car.tuv_valid_month ? `${car.tuv_valid_year}. ${car.tuv_valid_month}. hó` : String(car.tuv_valid_year),
        }
      : null,
    { icon: Gauge, label: "Futott km", value: formatKm(car.mileage) },
    car.trunk_liters ? { icon: CarIcon, label: "Csomagtartó", value: `${car.trunk_liters} l` } : null,
  ]
    .filter(Boolean)
    .concat(car.color ? [{ icon: CarIcon, label: "Szín", value: car.color + (car.color_metallic ? " (metál)" : "") }] : []);

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white">
      <Navbar />
      <section className="pt-28 pb-20" data-testid="car-detail-page">
        <div className={SITE_CONTAINER}>
          <Link to="/#keszlet" data-testid="detail-back" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Vissza a készlethez
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-6">
            {/* Gallery */}
            <div className="lg:col-span-3">
              {has360 && (
                <div className="inline-flex items-center gap-1 mb-3 p-0.5 rounded-full border border-white/10 bg-white/[0.03]" data-testid="detail-view-toggle">
                  <button
                    data-testid="detail-view-gallery"
                    onClick={() => setView("gallery")}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "gallery" ? "bg-electric text-white" : "text-slate-400 hover:text-white"}`}
                  >
                    <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.75} /> Galéria
                  </button>
                  <button
                    data-testid="detail-view-360"
                    onClick={() => setView("360")}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "360" ? "bg-electric text-white" : "text-slate-400 hover:text-white"}`}
                  >
                    <RotateCw className="w-3.5 h-3.5" strokeWidth={1.75} /> 360°
                  </button>
                </div>
              )}

              {has360 && view === "360" ? (
                <Viewer360 frames={frames360} autoRotate rotateIntervalMs={180} />
              ) : (
                <div className="relative aspect-[16/10] rounded-3xl overflow-hidden bg-anthracite-800">
                  {gallery.map((src, i) => (
                    <img
                      key={i}
                      src={proxied(src)}
                      alt={`${car.make} ${car.model} – ${i + 1}. nézet`}
                      referrerPolicy="no-referrer"
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"}`}
                    />
                  ))}
                </div>
              )}
              {view === "gallery" && gallery.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                  {gallery.map((src, i) => (
                    <button
                      key={i}
                      data-testid={`detail-thumb-${i}`}
                      onClick={() => setIdx(i)}
                      className={`shrink-0 w-24 h-16 rounded-xl overflow-hidden border-2 transition-all ${i === idx ? "border-electric" : "border-transparent opacity-60 hover:opacity-100"}`}
                    >
                      <img src={proxied(src)} alt={`nézet ${i + 1}`} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info panel */}
            <div className="lg:col-span-2">
              <div className="glass rounded-3xl p-6 lg:sticky lg:top-24">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs tracking-[0.15em] uppercase text-slate-400">{car.make} · {yearLabel}</div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-light mt-1" data-testid="detail-listing-title">
                      {car.listing_title?.trim() || car.model}
                    </h1>
                    {car.is_test_listing && (
                      <div className="mt-3">
                        <TestListingBadge />
                        <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                          Teszt hirdetés — nem valós készlet, csak a platform bemutatásához.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid="detail-favorite-btn"
                      onClick={async () => {
                        const res = await toggleFavorite(car);
                        if (!res) return;
                        setCar((c) => (c ? { ...c, favorite_count: res.favorite_count } : c));
                        toast.success(res.favorited ? "Elmentve a kedvencek közé" : "Eltávolítva a kedvencek közül");
                      }}
                      className={`w-10 h-10 rounded-full glass-strong flex items-center justify-center transition-all ${fav ? "text-red-400" : "text-slate-300 hover:text-white"}`}
                    >
                      <Heart className="w-4.5 h-4.5" fill={fav ? "currentColor" : "none"} strokeWidth={1.5} />
                    </button>
                    <button
                      data-testid="detail-share-btn"
                      onClick={share}
                      title="Link másolása"
                      className="w-10 h-10 rounded-full glass-strong flex items-center justify-center text-slate-300 hover:text-white transition-all"
                    >
                      <Share2 className="w-4.5 h-4.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>

                <div className="mt-4" data-testid="detail-price-wrap">
                  {car.sale_price && car.sale_price < car.price ? (
                    <>
                      <div className="text-sm text-slate-500 line-through">{formatPrice(car.price)}</div>
                      <div data-testid="detail-price" className="font-heading text-3xl font-light text-electric">{formatPrice(car.sale_price)}</div>
                    </>
                  ) : (
                    <div data-testid="detail-price" className="font-heading text-3xl font-light text-electric">{formatPrice(car.price)}</div>
                  )}
                  {car.vat_reclaimable && (
                    <div className="text-xs text-emerald-400/90 mt-1" data-testid="detail-vat">ÁFA visszaigényelhető</div>
                  )}
                </div>
                {(car.finance_available || car.lease_available) && (
                  <div className="mt-3 text-sm text-slate-400 space-y-1" data-testid="detail-finance">
                    {car.finance_available && car.finance_monthly && (
                      <p>Havi törlesztő: {formatPrice(car.finance_monthly)}{car.finance_months ? ` · ${car.finance_months} hó` : ""}</p>
                    )}
                    {car.lease_available && <p>Bérlési lehetőség</p>}
                  </div>
                )}
                {(car.trade_in_possible || car.immediate_available === false) && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {car.trade_in_possible && (
                      <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">Beszámítás</span>
                    )}
                    {car.immediate_available === false && (
                      <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">Előrendelhető</span>
                    )}
                  </div>
                )}

                <FavoriteCount count={favCount} testid="detail-favorite-count" className="mt-3" />

                {(car.address || car.city || car.county) && (
                  <div className="mt-4" data-testid="detail-location">
                    <ListingMap car={car} />
                  </div>
                )}

                {car.description && <p className="mt-4 text-slate-300 text-sm leading-relaxed">{car.description}</p>}

                <ListingDealerPeek car={car} open={Boolean(car?.id)} testid="detail-dealer-peek" />

                <div className="mt-6 space-y-3">
                  <button
                    data-testid="detail-chat-btn"
                    onClick={startChat}
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-electric hover:bg-electric-light text-white font-medium transition-all hover:blue-glow"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Érdekel – írok az eladónak
                  </button>
                  <ListingSellerPhone car={car} testid="detail-seller-phone" />
                </div>
              </div>
            </div>
          </div>

          {/* Specs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10" data-testid="detail-specs">
            {specs.map((s) => (
              <div key={s.label} className="glass rounded-xl p-4">
                <s.icon className="w-4 h-4 text-electric mb-2" strokeWidth={1.5} />
                <div className="text-[10px] tracking-wide uppercase text-slate-400">{s.label}</div>
                <div className="text-sm font-medium mt-0.5">{s.value}</div>
              </div>
            ))}
          </div>

          <CarFeaturesDisplay features={car.features} />

          {/* Similar cars */}
          {similar.length > 0 && (
            <div className="mt-16" data-testid="similar-cars-section">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-electric" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl font-light">Hasonló autók</h2>
              </div>
              <div className={LISTING_CAR_GRID}>
                {similar.map((c) => (
                  <CarCard key={c.id} car={c} compact onQuickView={(x) => navigate(carPath(x))} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
